import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, KeyRound, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Reset link is missing the token");
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — please sign in");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white grid lg:grid-cols-2">
      <div className="hidden lg:flex relative grid-bg-brand p-12 flex-col justify-between border-r border-[#1c1c1c]">
        <div className="font-heading text-lg font-bold uppercase tracking-tight">Chongie Aviation</div>
        <div className="max-w-md">
          <div className="label-eyebrow mb-3" style={{ color: "#8a83d4" }}>FINAL STEP</div>
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight leading-tight">
            Set your<br />new password.
          </h1>
          <p className="mt-6 text-sm text-[#b9b2e8] max-w-sm">
            Pick a strong password you don't use elsewhere. Minimum 8 characters.
          </p>
        </div>
        <div className="font-mono text-xs text-[#666]">JWT • bcrypt • audit logged</div>
      </div>

      <div className="relative flex items-center justify-center p-8 lg:p-16 bg-brand-vertical">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <form onSubmit={submit} className="relative w-full max-w-md space-y-6" data-testid="reset-password-form">
          <Link to="/login" className="inline-flex items-center text-xs font-mono text-[#888] hover:text-white">
            <ArrowLeft className="h-3 w-3 mr-1" /> Back to sign in
          </Link>
          <div>
            <div className="label-eyebrow">ACCOUNT RECOVERY</div>
            <h2 className="font-heading text-3xl uppercase font-bold mt-2">New password</h2>
          </div>

          {!token && (
            <div className="border border-red-500/40 bg-red-500/5 p-4 text-sm">
              <div className="font-heading uppercase tracking-wider text-red-300">Reset link incomplete</div>
              <p className="text-xs font-mono text-red-300/80 mt-2">No token found. Request a fresh reset link.</p>
            </div>
          )}

          <PwField id="pw" label="NEW PASSWORD" value={pw} set={setPw} testid="rp-new" />
          <PwField id="cf" label="CONFIRM NEW PASSWORD" value={confirm} set={setConfirm} testid="rp-confirm" />

          <Button
            type="submit" disabled={busy || !token}
            data-testid="rp-submit"
            className="w-full h-12 rounded-none btn-brand border border-[#3b2bd0] font-heading font-bold uppercase tracking-[0.15em]"
          >
            <KeyRound className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Set new password"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function PwField({ id, label, value, set, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="label-eyebrow">{label}</Label>
      <div className="relative mt-2">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666]" />
        <Input
          id={id}
          type="password"
          value={value}
          onChange={(e) => set(e.target.value)}
          required
          data-testid={testid}
          className="pl-10 bg-black border-[#222] rounded-none h-12 font-mono text-sm"
        />
      </div>
    </div>
  );
}
